/* HAIRKA — small progressive enhancements. No dependencies. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Reveal-on-scroll for .hk-reveal elements */
  const revealAll = (root = document) => {
    const items = root.querySelectorAll('.hk-reveal:not(.is-revealed)');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    items.forEach((el) => io.observe(el));
  };

  /*
   * Sticky buy bar. Shows once the product's buy area has scrolled out of view, and scrolls back
   * to it on click. The target is, in order: the EasySell form, the hk_buy_anchor block, the
   * product form. It never submits anything itself.
   */
  class HkStickyBuy extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-hk-sticky-button]');
      this.watch =
        document.querySelector(this.dataset.watch || '') ||
        document.querySelector('[data-hk-buy-anchor]') ||
        document.querySelector('[id^="shopify-block-"][id*="easysell"], #easysell');
      if (this.button) this.button.addEventListener('click', this.onClick.bind(this));
      if (!this.watch || !('IntersectionObserver' in window)) {
        this.classList.add('is-visible');
        return;
      }
      this.observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', !entry.isIntersecting && scrolledPast);
      });
      this.observer.observe(this.watch);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    target() {
      return (
        document.getElementById('es-form-container') ||
        document.getElementById('easysell') ||
        document.querySelector('[data-hk-buy-anchor]') ||
        document.querySelector('product-form') ||
        this.watch
      );
    }

    onClick(event) {
      const target = this.target();
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      const focusable = target.querySelector('input, select, button');
      if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), reduceMotion ? 0 : 600);
    }
  }
  if (!customElements.get('hk-sticky-buy')) customElements.define('hk-sticky-buy', HkStickyBuy);

  /* Timeline: activates the step closest to the middle of the viewport, or the clicked step. */
  class HkTimeline extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('[data-hk-step]'));
      if (!this.steps.length) return;
      this.steps.forEach((step, index) => {
        step.addEventListener('click', () => this.activate(index));
      });
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.activate(this.steps.indexOf(entry.target));
          });
        },
        { rootMargin: '-45% 0px -45% 0px' }
      );
      this.steps.forEach((step) => this.observer.observe(step));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    activate(index) {
      if (index < 0) return;
      this.steps.forEach((step, i) => {
        const active = i === index;
        step.classList.toggle('is-active', active);
        step.classList.toggle('is-done', i < index);
        step.setAttribute('aria-current', active ? 'step' : 'false');
      });
      this.querySelectorAll('[data-hk-step-media]').forEach((media) => {
        media.hidden = media.dataset.hkStepMedia !== String(index) && media.dataset.hkStepMedia !== 'all';
      });
    }
  }
  if (!customElements.get('hk-timeline')) customElements.define('hk-timeline', HkTimeline);

  document.addEventListener('DOMContentLoaded', () => revealAll());
  document.addEventListener('shopify:section:load', (event) => revealAll(event.target));
})();
